<template>
  <div class="jd-card" :class="{ 'jd-highlighted': isApplicantJd }">
    <!-- Top Part: Title and Radio -->
    <div class="d-flex justify-content-between align-items-start mb-2">
      <div class="flex-grow-1 pe-3" style="min-width: 0;">
        <div class="d-flex align-items-center mb-1" style="min-width: 0;">
          <p
            v-html="highlightedJobTitle"
            class="clickable job-title mb-0 text-truncate-single"
            @click="openJdInfo(jd)"
          ></p>
        </div>
      </div>
      
      <!-- Selection Radio -->
      <div class="radio-column" @click="handleRadioClick(jd, isApplicantJd, $event)">
        <div class="custom-radio-circle" :class="{ checked: isApplicantJd }">
          <span
            v-if="!isApplicantJd"
            class="custom-radio-label"
          >
            選択
          </span>
        </div>
      </div>
    </div>

    <!-- Inner Content Box -->
    <div class="jd-inner-box" @click="openJdInfo(jd)">
      <div class="field-item">
        <label class="field-label">職種</label>
        <p class="field-text text-truncate-single">{{ jd.job_title }}</p>
      </div>
      
      <div class="field-item">
        <label class="field-label">勤務地</label>
        <p class="field-text text-truncate-single">{{ jd.location || '-' }}</p>
      </div>
      
      <div class="field-item">
        <label class="field-label">募集背景</label>
        <div class="field-text" v-html="highlightedQualification || '-'"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JDRow',
  props: ['jd', 'jdClass', 'isSubmitting', 'profile', 'applicantJD','radioName'],
  computed: {
    isApplicantJd() {
      return this.jdClass === 'highlighted' || 
            (this.applicantJD && this.applicantJD._id === this.jd._id);
    },
    isScoutedJd() {
      const { scouted_jobs } = this.profile;
      return scouted_jobs.some((jobDetails) => jobDetails.job === this.jd._id);
    },
    scoutedJobStatus() {
      if (!this.profile || !this.profile.scouted_jobs) return null;
      const scoutedJob = this.profile.scouted_jobs.find(
        (jobDetails) => jobDetails.job === this.jd._id
      );
      return scoutedJob ? scoutedJob.status : null;
    },
    isScoutedExpired() {
      return this.scoutedJobStatus === 'expired';
    },
    scoutedButtonText() {
      if (this.isScoutedExpired) {
        return 'スカウト希望期間終了';
      }
      return 'スカウト希望中';
    },
    matchButtonClass() {
      return this.jdClass === 'highlighted' ? 'match' : '';
    },
    matchButtonText() {
      return this.jdClass === 'highlighted' ? '選択解除' : '選択';
    },
    highlightedJobTitle() {
      if (!this.jd.highlights || !this.jd.highlights.length) {
        return this.jd.job_title;
      }

      const highlight = this.jd.highlights.find((h) => h.path === 'job_title');
      if (!highlight || !highlight.texts) {
        return this.jd.job_title;
      }

      const hits = highlight.texts
        .filter((text) => text.type === 'hit')
        .map((text) => text.value)
        .sort((a, b) => b.length - a.length);

      return this.highlightHits(
        this.highlightFullWidthSpaces(this.jd.job_title, hits),
        hits
      );
    },
    highlightedQualification() {
      return this.processJobQualification();
    },
  },
  methods: {
    openJdInfo(jd) {
      this.$emit('openJdInfo', jd);
    },
    handleRadioClick(jd, currentState, event) {
      if (this.isSubmitting) return;
      if (currentState) {
        event.target.checked = false;
        this.matchJd(jd, false);
      } else {
        this.matchJd(jd, true);
      }
    },
    matchJd(jd) {
      this.$emit('matchJd', jd, this.isApplicantJd);
    },
    processJobQualification() {
      if (!this.jd.highlights || !this.jd.highlights.length) {
        return this.jd.qualification;
      }

      const highlight = this.jd.highlights.find(
        (h) => h.path === 'qualification'
      );
      if (!highlight || !highlight.texts) {
        return this.jd.qualification;
      }

      // Get all hit values and sort them by length
      const hitTexts = highlight.texts
        .filter((text) => text.type === 'hit')
        .sort((a, b) => b.value.length - a.value.length);

      // Check if any hit contains HTML or if the job description contains HTML
      if (
        hitTexts.some((text) => text.value.includes('<')) ||
        this.jd.job_description.includes('<')
      ) {
        // Handle HTML content
        let fullContent = this.jd.qualification;

        hitTexts.forEach((text) => {
          const escapedValue = this.escapeRegExp(text.value);
          // Create regex that doesn't match inside HTML tags
          const regex = new RegExp(`(${escapedValue})(?![^<]*>)`, 'g');
          fullContent = fullContent.replace(
            regex,
            `<span class="highlight-hit">$1</span>`
          );
        });

        return fullContent;
      } else {
        // Handle plain text while preserving non-hit texts
        let result = '';
        highlight.texts.forEach((text) => {
          if (text.type === 'hit') {
            // Find the corresponding sorted hit text
            const sortedHit = hitTexts.find((hit) => hit.value === text.value);
            result += `<span class="highlight-hit">${sortedHit.value}</span>`;
          } else {
            result += text.value;
          }
        });

        return result;
      }
    },
    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    highlightFullWidthSpaces(text, hits) {
      // Create patterns for all possible hit combinations with full-width space
      const patterns = hits.flatMap((hit1, index) =>
        hits.slice(index + 1).map((hit2) => ({
          pattern: new RegExp(
            `(${this.escapeRegExp(hit1)}　${this.escapeRegExp(
              hit2
            )})|(${this.escapeRegExp(hit2)}　${this.escapeRegExp(hit1)})`,
            'g'
          ),
          replacement: (_, g1, g2) => {
            const match = g1 || g2;
            const [first, second] = match.split('　');
            return `${first}<span class="highlight-hit">　</span>${second}`;
          },
        }))
      );

      // Apply all patterns at once
      return patterns.reduce(
        (text, { pattern, replacement }) => text.replace(pattern, replacement),
        text
      );
    },

    highlightHits(text, hits) {
      const highlightedPositions = new Set();

      return hits.reduce((text, hit) => {
        const pattern = new RegExp(this.escapeRegExp(hit), 'g');
        return text.replace(pattern, (match, offset) => {
          const positions = Array.from(
            { length: match.length },
            (_, i) => offset + i
          );

          if (positions.every((pos) => !highlightedPositions.has(pos))) {
            positions.forEach((pos) => highlightedPositions.add(pos));
            return `<span class="highlight-hit">${match}</span>`;
          }
          return match;
        });
      }, text);
    },
    formatHighlightText(texts) {
      if (texts.some((text) => text.value.includes('<'))) {
        // Handle HTML content
        let fullContent = this.jd.qualification;
        texts.forEach((text) => {
          if (text.type === 'hit') {
            // Escape special regex characters in the text value
            const escapedValue = text.value.replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&'
            );
            // Create regex that doesn't match inside HTML tags
            const regex = new RegExp(`(${escapedValue})(?![^<]*>)`, 'g');
            fullContent = fullContent.replace(
              regex,
              `<span class="highlight-hit">$1</span>`
            );
          }
        });
        return fullContent;
      } else {
        // Handle plain text (existing logic for job_title)
        return texts
          .map((text) => {
            if (text.type === 'hit') {
              return `<span class="highlight-hit">${text.value}</span>`;
            }
            return text.value;
          })
          .join('');
      }
    },
  },
};
</script>

<style scoped>
.jd-card {
  border: 1px solid #787878;
  border-radius: 15px;
  padding: 10px;
  background-color: #fff;
  margin-bottom: 15px;
  position: relative;
  transition: all 0.2s;
  height: 155px;
}

.jd-highlighted {
  border-color: #2F80ED;
  background-color: #F0F7FF;
}

.jd-inner-box {
  border: 1px solid #787878;
  border-radius: 8px;
  padding: 12px;
  background-color: #fff;
  max-height: 104px;
  overflow-y: auto;
  cursor: pointer;
}

/* Custom scrollbar styling */
.jd-inner-box::-webkit-scrollbar {
  width: 8px;
}

.jd-inner-box::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 8px;
  margin: 4px 0;
}

.jd-inner-box::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 8px;
  border: 2px solid #fff;
  background-clip: padding-box;
}

.jd-inner-box::-webkit-scrollbar-thumb:hover {
  background: #555;
  border: 2px solid #fff;
  background-clip: padding-box;
}

.field-label {
  text-decoration: underline;
  font-weight: bold;
  font-size: 12px;
  margin-bottom: 5px;
  display: block;
}

.field-text {
  font-size: 12px;
  color: #333;
  margin-bottom: 10px;
  line-height: 1.4;
}

.job-title {
  font-weight: bold;
  font-size: 16px;
  color: #000;
}

.text-truncate-single {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  width: 100%;
}

.radio-column {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 40px;
}

.custom-radio-circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #ccc;
  position: relative;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 7px;
  color: #9e9e9e;
}

.checked.custom-radio-circle {
  border-color: #2F80ED;
  background-color: #2F80ED; /* Blue background */
}

.checked.custom-radio-circle::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 10px; /* Smaller white hole */
  height: 10px;
  background-color: #fff; /* White hole */
  border-radius: 50%;
}

.custom-radio-label {
  pointer-events: none;
}

.radio-label-text {
  font-size: 10px;
  color: #666;
}

.scouted-btn {
  min-width: 120px;
  height: 24px;
  font-weight: bold;
  font-size: 10px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #E0F2F1;
  color: #14B8A6;
  border: 1px solid #14B8A6;
  padding: 0 8px;
}

.scouted-btn-expired {
  background-color: #E0E0E0;
  color: #757575;
  border-color: #BDBDBD;
}

.clickable {
  cursor: pointer;
}

.highlight-hit {
  background-color: rgba(255, 235, 59, 0.5);
  border-radius: 2px;
}
</style>
